import { ArrowUpRight, MessageSquareText, Phone } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const CALENDLY = "https://calendly.com/thedustincampos/1hr-in-person";

export default function Consult() {
  const { t } = useLang();
  const smsHref = `sms:+18082984096?&body=${encodeURIComponent(t.consult.smsBody)}`;

  return (
    <section id="consult" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <Reveal className="mb-14 max-w-3xl">
          <Eyebrow className="mb-5">{t.consult.eyebrow}</Eyebrow>
          <h2 className="font-display text-4xl font-semibold leading-[1.05] text-cream sm:text-5xl lg:text-6xl">
            {t.consult.titleA}
            <br />
            <span className="italic text-gold-grad">{t.consult.titleB}</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-cream/70">
            {t.consult.intro}
          </p>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-gold/20 bg-navy2/60 p-2 sm:p-3" data-testid="calendly-embed-container">
              <iframe
                src={`${CALENDLY}?hide_gdpr_banner=1&background_color=0b192c&text_color=f8fafc&primary_color=d4af37`}
                title={t.consult.iframeTitle}
                className="h-[640px] w-full rounded-2xl border-0 sm:h-[720px]"
                loading="lazy"
                data-testid="calendly-embed"
              />
            </div>
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-calendly-button"
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-cream/60 transition-colors duration-300 hover:text-gold"
            >
              {t.consult.openCalendly}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>

          <div className="flex flex-col gap-6">
            <Reveal delay={0.12}>
              <div className="card-lift rounded-3xl border border-gold/25 bg-cream p-8 text-ink" data-testid="sms-qr-card">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-ink/55">{t.consult.qrLabel}</p>
                <a
                  href={smsHref}
                  data-testid="sms-qr-code-button"
                  className="group mt-6 block"
                  aria-label={t.consult.smsAria}
                >
                  <div className="relative mx-auto max-w-[240px] rounded-2xl border-2 border-ink/10 bg-cream p-4 transition-transform duration-300 group-hover:scale-[1.03]">
                    <img src="/sms-qr.png" alt="QR code to text Dustin Campos" className="mx-auto h-auto w-full" />
                    <span className="absolute left-1/2 top-0 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ink shadow-lg">
                      <MessageSquareText className="h-4 w-4 text-gold" />
                    </span>
                  </div>
                </a>
                <p className="mt-6 text-center font-display text-2xl font-semibold tracking-wide">808-298-4096</p>
                <p className="mt-2 text-center text-xs leading-relaxed text-ink/55">
                  {t.consult.qrHint}
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <a
                href="tel:+18085004711"
                data-testid="consult-call-card"
                className="card-lift flex items-center gap-5 rounded-3xl border border-gold/20 bg-navy2/70 p-7"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
                  <Phone className="h-5 w-5 text-gold" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.26em] text-cream/50">
                    {t.consult.callLabel}
                  </span>
                  <span className="mt-1 block font-display text-2xl font-semibold text-cream">808-500-4711</span>
                </span>
              </a>
            </Reveal>

            <Reveal delay={0.28}>
              <div className="rounded-3xl border border-cream/10 bg-ink/60 p-7" data-testid="consult-what-to-expect">
                <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-gold">{t.consult.expectTitle}</p>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-cream/65">
                  {t.consult.expect.map((line) => (
                    <li key={line} className="flex gap-3">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rotate-45 bg-gold" />
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
